import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Loader2, CheckCircle, LogOut } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { apiPost } from '../utils/api';

/**
 * Full-page gate shown to logged-in users who haven't verified their email.
 * The only actions available are: resend the email, or log out. All app
 * navigation is blocked until verification completes.
 */
export default function VerificationPending() {
  const { currentUser, logout, refreshUser } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sentMessage, setSentMessage] = useState('');
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  const isDark = theme === 'dark';

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  // Periodically refresh the user object so the page auto-exits once they verify
  // (e.g., they clicked the email link in another tab).
  useEffect(() => {
    if (!refreshUser) return;
    const id = setInterval(() => refreshUser(), 10_000);
    return () => clearInterval(id);
  }, [refreshUser]);

  const handleResend = async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    setError('');
    setSentMessage('');
    try {
      const res = await apiPost('/auth/resend_verification', {});
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSentMessage(data.message || 'Verification email sent.');
        setCooldown(60);
        if (refreshUser) refreshUser();
      } else if (res.status === 429) {
        setError('Too many requests. Please wait a bit before trying again.');
      } else {
        setError(data.error || 'Could not send the email. Try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-6 ${
      isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      <div className={`max-w-md w-full rounded-xl p-8 ${
        isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
      }`}>
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Mail className="w-7 h-7 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        <h1 className="text-2xl font-semibold text-center mb-2 font-unna">Verify your email</h1>
        <p className={`text-center text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          We sent a verification link to<br />
          <span className="font-medium">{currentUser?.email}</span>
        </p>

        <div className={`rounded-lg p-4 mb-6 text-sm ${
          isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-gray-50 border border-gray-200'
        }`}>
          <p className={isDark ? 'text-gray-300' : 'text-gray-700'}>
            Click the link in that email to activate your account. Until then, the rest of JPro is locked.
          </p>
          <p className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Didn't get it? Check your spam folder, or resend below.
          </p>
        </div>

        {sentMessage && (
          <div className="flex items-center gap-2 p-3 mb-3 text-sm rounded-lg bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <span>{sentMessage}</span>
          </div>
        )}
        {error && (
          <div className="p-3 mb-3 text-sm rounded-lg bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}

        <button
          onClick={handleResend}
          disabled={sending || cooldown > 0}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-2 text-sm font-rasa bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {sending ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
          ) : cooldown > 0 ? (
            <>Resend in {cooldown}s</>
          ) : (
            <><Mail className="w-4 h-4" /> Resend verification email</>
          )}
        </button>

        <button
          onClick={handleLogout}
          className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-rasa rounded-lg border ${
            isDark
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
              : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </div>
  );
}
